/**
 * Requests that go AROUND the Supabase client but carry the signed-in person's
 * token: a staff screen's direct Edge Function call (to read the body of an
 * error), a photo upload, the Cataloging app's native file upload, our own
 * services (the Intelligence Engine, the copilot).
 *
 * They get the SAME rule as every request through the client (move P14,
 * `refresh-on-401.ts`): one refresh and one retry on a 401, the shared refresh,
 * the cooldown, never a token of somebody else's. The rule lives once — this
 * file only reads the token at the moment of sending and asks the client's own
 * rule what to retry with (memory: no mechanical divergence).
 *
 * The token is read per attempt, never carried: a queue that sat overnight, or
 * a screen open for an hour, holds a token that has since been replaced.
 */
import type { AuctisyncSupabase } from './client.js';
import { discard, isReplayable, personOfToken } from './refresh-on-401.js';
import { sessionToolsOf } from './session-tools.js';

/** Thrown by `fetchWithSession` / `fetchEdgeFunction` when no one is signed in at the moment of the request. */
export class SignedOutError extends Error {
  constructor() {
    super('You need to be signed in.');
    this.name = 'SignedOutError';
  }
}

export type SessionSend<R> =
  /** Sent; `result` is the final answer (the retry's, when there was one). */
  | { outcome: 'sent'; result: R }
  /** Nobody is signed in: nothing was sent. */
  | { outcome: 'signed_out' }
  /** Someone other than `onlyFor` is signed in: nothing was sent. */
  | { outcome: 'someone_else' };

export interface SendWithSessionOptions {
  /**
   * Send only while THIS person (their user id) is signed in. Work queued by
   * one person on a shared device must not go out under the next person's name.
   */
  onlyFor?: string;
  /**
   * False when the request cannot be sent twice (a body that is a stream).
   * Defaults to true. A function is asked after the first answer, at the
   * moment of the retry (a caller may have walked away by then).
   */
  retry?: boolean | (() => boolean);
}

function retryAllowed(retry: SendWithSessionOptions['retry']): boolean {
  return typeof retry === 'function' ? retry() : retry !== false;
}

function release(result: unknown): void {
  if (typeof Response !== 'undefined' && result instanceof Response) discard(result);
}

/**
 * Send with the signed-in person's current token; on a 401, ask the client's
 * retry rule for a token and send once more. `send` may be anything that
 * answers with a status — a `fetch`, a native upload.
 */
export async function sendWithSession<R extends { status: number }>(
  supabase: AuctisyncSupabase,
  send: (token: string) => Promise<R>,
  options: SendWithSessionOptions = {},
): Promise<SessionSend<R>> {
  const { retryToken } = sessionToolsOf(supabase);
  const token = (await supabase.auth.getSession()).data.session?.access_token ?? null;
  if (token === null) return { outcome: 'signed_out' };
  if (options.onlyFor !== undefined && personOfToken(token) !== options.onlyFor) {
    return { outcome: 'someone_else' };
  }

  const first = await send(token);
  if (first.status !== 401 || !retryAllowed(options.retry))
    return { outcome: 'sent', result: first };

  const fresh = await retryToken(token);
  if (fresh === null) return { outcome: 'sent', result: first };
  release(first);
  // The second answer is final, whatever it is.
  return { outcome: 'sent', result: await send(fresh) };
}

export interface FetchWithSessionOptions {
  /** The fetch to use. Defaults to the global one at the moment of the call. */
  fetch?: typeof fetch;
  /** Send only while this person (user id) is signed in — see `SendWithSessionOptions`. */
  onlyFor?: string;
}

/**
 * `fetch` carrying the signed-in person's token, with the shared retry, saying
 * rather than throwing when nothing could be sent.
 *
 * No timeout of its own: these callers upload photos and wait on AI calls that
 * can run past the client's 20 s request limit, exactly as they did before.
 */
export async function sendFetchWithSession(
  supabase: AuctisyncSupabase,
  input: string,
  init: RequestInit = {},
  options: FetchWithSessionOptions = {},
): Promise<SessionSend<Response>> {
  const doFetch: typeof fetch = options.fetch ?? ((...args) => fetch(...args));
  const base = Object.fromEntries(new Headers(init.headers).entries());
  return sendWithSession(
    supabase,
    (token) => doFetch(input, { ...init, headers: { ...base, authorization: `Bearer ${token}` } }),
    {
      // A caller that has walked away does not want a refresh or a second
      // request: read when the 401 is in hand, as the transport does.
      retry: () => isReplayable(init.body) && init.signal?.aborted !== true,
      ...(options.onlyFor !== undefined ? { onlyFor: options.onlyFor } : {}),
    },
  );
}

/**
 * `fetch` carrying the signed-in person's token, with the shared retry.
 * Rejects with `SignedOutError` when nothing could be sent (no one signed in,
 * or someone other than `onlyFor`).
 */
export async function fetchWithSession(
  supabase: AuctisyncSupabase,
  input: string,
  init: RequestInit = {},
  options: FetchWithSessionOptions = {},
): Promise<Response> {
  const sent = await sendFetchWithSession(supabase, input, init, options);
  if (sent.outcome !== 'sent') throw new SignedOutError();
  return sent.result;
}

/** The address of one Edge Function, at the functions address the client was built with. */
export function edgeFunctionUrl(supabase: AuctisyncSupabase, name: string): string {
  return `${sessionToolsOf(supabase).functionsUrl}/${encodeURIComponent(name)}`;
}

/**
 * Call an Edge Function directly — for a caller that needs the raw `Response`
 * (an error body `functions.invoke` would hide, a multipart upload) — with the
 * person's token, the anon key and the shared retry; says rather than throws
 * when nothing could be sent.
 */
export async function sendEdgeFunction(
  supabase: AuctisyncSupabase,
  name: string,
  init: RequestInit = {},
  options: FetchWithSessionOptions = {},
): Promise<SessionSend<Response>> {
  const headers = new Headers(init.headers);
  if (!headers.has('apikey')) headers.set('apikey', sessionToolsOf(supabase).anonKey);
  return sendFetchWithSession(
    supabase,
    edgeFunctionUrl(supabase, name),
    { ...init, headers },
    options,
  );
}

/** `sendEdgeFunction`, rejecting with `SignedOutError` when nothing could be sent. */
export async function fetchEdgeFunction(
  supabase: AuctisyncSupabase,
  name: string,
  init: RequestInit = {},
  options: FetchWithSessionOptions = {},
): Promise<Response> {
  const sent = await sendEdgeFunction(supabase, name, init, options);
  if (sent.outcome !== 'sent') throw new SignedOutError();
  return sent.result;
}
